import { useMemo } from 'react'
import {
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  TextField as MuiTextField,
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material'
import { type MinimalSxProp } from '../../types'

type BaseProps = Pick<
  MuiTextFieldProps,
  | 'autoComplete'
  | 'autoCorrect'
  | 'autoFocus'
  | 'defaultValue'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'helperText'
  | 'hiddenLabel'
  | 'id'
  | 'inputMode'
  | 'inputRef'
  | 'label'
  | 'maxRows'
  | 'multiline'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onPaste'
  | 'placeholder'
  | 'ref'
  | 'required'
  | 'rows'
  | 'slotProps'
  | 'translate'
  | 'type'
  | 'value'
> &
  MinimalSxProp & {
    readOnly?: boolean
  }

export type TextFieldSmallProps = Omit<BaseProps, 'label'> & {
  /**
   * When you need a smaller size use small. Be aware you can not have a label with this option small.
   * @default: medium
   */
  size?: keyof Pick<Record<NonNullable<MuiTextFieldProps['size']>, unknown>, 'small'>
}

export type TextFieldMediumLargeProps = BaseProps & {
  /**
   * When you need a bigger size use large.
   * @default: medium
   */
  size?: keyof Pick<Record<NonNullable<MuiTextFieldProps['size']>, unknown>, 'large' | 'medium'>
}

export type TextFieldProps = TextFieldSmallProps | TextFieldMediumLargeProps

export const TextField = ({ sx, readOnly, slotProps, ...rest }: TextFieldProps) => {
  const mergedSlotProps = useMemo(
    () => ({
      ...slotProps,
      input: {
        ...slotProps?.input,
        readOnly,
      },
    }),
    [slotProps, readOnly]
  )
  return <MuiTextField {...rest} slotProps={mergedSlotProps} sx={sx} />
}
